import clsx from "clsx";
import rg4js from "raygun4js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Container, Modal } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import {
  type Case,
  type Client,
  CommunicationStatus,
  DocumentStatus,
  DocumentSubType,
  DocumentType,
  PropoundingParty,
  Role,
  SubscriptionStatus,
} from "@/briefpoint-client";
import DocumentList from "@/components/DocumentList/DocumentList";
import EmptyList from "@/components/DocumentList/EmptyList";
import ProcessingBox from "@/components/DocumentList/ProcessingBox";
import Help from "@/components/Help";
import Loading from "@/components/Loading";
import IntroTutorialModal from "@/components/Modals/IntroTutorialModal";
import SignUpModal from "@/components/Modals/SignUpModal";
import TrialEndsBanner, {
  GetTrialBannerMessage,
} from "@/components/TrialEndsBanner";
import { useAuth } from "@/hooks/useAuth";
import useDocuments from "@/hooks/useDocuments";
import useFirm from "@/hooks/useFirm";
import { useInterval as UseInterval } from "@/hooks/useInterval";
import type {
  CaseResponse,
  ClientResponse,
  DocumentClient,
  ViewCommunicationResponse,
} from "@/lib/types";
import {
  hasCanceledSubscription,
  isInTrialOrHasActiveSubscription,
  normalizeError,
  sanitizeFiles,
} from "@/lib/utils";
import "./DocumentList.scss";
import { useMutation } from "@tanstack/react-query";
import { HashLink as Link } from "react-router-hash-link";
import AddEditCaseModal from "@/components/CaseManagement/AddEditCaseModal";
import GeneralInformationScreen from "@/components/ConfirmInfo/GeneralInformation";
import { blankId } from "@/components/ConfirmInfo/SourceDocumentReview";
import ExternalFileBrowserSelect from "@/components/ExternalPartner/ExternalFileBrowserSelect";
import ExternalPartnerActions from "@/components/ExternalPartnerActions/ExternalPartnerActions";
import LeavingGuard from "@/components/LeavingGuard";
import PropoundSelectionModal from "@/components/Modals/PropoundSelectionModal";
import RespondToDiscoveryModal from "@/components/Modals/RespondToDiscoveryModal";
import { UploadProductionModal } from "@/components/Modals/UploadProductionModal";
import { TopRibbon } from "@/components/TopRibbon/TopRibbon";
import { toast } from "@/components/ui/toast";
import {
  useCaseApi,
  useClientApi,
  useCommunicationApi,
  useDocumentApi,
  useFirmApi,
  useTagApi,
} from "@/hooks/useApi";
import { CaliforniaId, SEARCH_PARAMS } from "@/lib/constants";
import { loadTags } from "@/utils/Requests/utils";
import RequiredCaseInfo from "./DocumentGeneration/RequiredCaseInfo";

export function getUploadingDocument(name: string): DocumentClient {
  return {
    id: `${name}-${Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, "")
      .slice(0, 5)}`,
    name,
    info: {
      documentType: DocumentType.Unknown,
      documentSubType: DocumentSubType.Unknown,
      venue: "",
      jurisdiction: "",
      propoundingParty: PropoundingParty.Unknown,
      propoundingPartyName: "",
      respondingPartyName: "",
      caseNumber: "",
      setNumber: 1,
      startingNumber: 1,
    },
    uploadDate: new Date(),
    status: DocumentStatus.Unknown,
    isArchived: false,
  } as DocumentClient;
}

const hasSeenSignUpModalStorageKey = "hasSeenSignUpModal";

export default function DocumentListScreen() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const documentApi = useDocumentApi();

  const [
    documents,
    loadDocuments,
    uploadFiles,
    ,
    ,
    updateDocument,
    downloadForm,
    ,
    downloadRequest,
  ] = useDocuments();
  const location = useLocation();
  const didSignUp =
    new URLSearchParams(location.search).get("signedup") ?? undefined;
  const [isSignUpModalShow, setIsSignUpModalShow] = useState(false);
  const [isSignUpModalQuery, setIsSignUpModalQuery] = useState(false);
  const [isIntroTutorialModalShow, setIsIntroTutorialModalShow] =
    useState(false);
  const [isOver, setIsOver] = useState(false);
  const { user, firm, jurisdictions, updatePreferences } = useAuth();
  const [allCases, setAllCases] = useState<Case[]>();
  const [allClients, setAllClients] = useState<Client[]>();
  const [showUploadDocs, setShowUploadDocs] = useState(false);
  const firmApi = useFirmApi();
  const clientApi = useClientApi();
  const caseApi = useCaseApi();
  const tagApi = useTagApi();
  const history = useHistory();
  const infoLoading = useRef(false);
  // QUICK ACTIONS
  const [users, , , ,] = useFirm(firm?.id, false);
  const [showPropoundIntroModal, setShowPropoundIntroModal] =
    useState<boolean>(false);
  const [isPleadingUpload, setIsPleadingUpload] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [isExternalEdit, setIsExternalEdit] = useState<boolean>(false);
  const [quickActionCase, setQuickActionCase] = useState<Case | undefined>();
  const [quickActionType, setQuickActionType] = useState<
    DocumentSubType | undefined
  >();
  const [quickActionIsSaving, setQuickActionIsSaving] =
    useState<boolean>(false);
  const [quickActionError, setQuickActionError] = useState<
    string | undefined
  >();
  const [showAddNewCase, setShowAddNewCase] = useState<boolean>(false);
  const [typeToGenerate, setTypeToGenerate] = useState<DocumentSubType>();
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const partyTags = useRef(new Map<number, string>());
  const pendingDraftType = useRef<DocumentSubType | undefined>(undefined);
  const [uploadingDocuments, setUploadingDocuments] = useState<
    DocumentClient[]
  >([]);
  const [isExternalUploading, setIsExternalUploading] =
    useState<boolean>(false);
  const communicationApi = useCommunicationApi();
  const [communications, setCommunications] = useState<
    ViewCommunicationResponse[] | undefined
  >();

  // Add loading state for tags to prevent duplicate requests
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isUploadingDiscovery =
    searchParams.get(SEARCH_PARAMS.PROPOUND_DISCOVERY) === "true";
  const isUploadingResponse =
    searchParams.get(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY) === "true";
  const isUploadingProduction =
    searchParams.get(SEARCH_PARAMS.UPLOAD_PRODUCTION) === "true";

  const archiveFilesMutation = useMutation({
    mutationFn: async (doc: DocumentClient) => {
      doc.isArchived = true;
      await documentApi.documentUpdateDocument({
        documentId: doc.id,
        document: doc,
      });
      return doc;
    },
    onSuccess: (doc: DocumentClient) => {
      toast.success(`${doc.name} was archived successfully.`);
    },
    onError: (error, doc: DocumentClient) => {
      toast.error(
        `There was an error archiving ${doc.name}. Please try again later.`,
      );
      rg4js("send", {
        error: normalizeError(error),
      });
    },
  });

  // Helper function to manage searchParams operations
  const updateSearchParam = useCallback(
    (param: string, value?: string) => {
      const newSearchParams = new URLSearchParams(location.search);
      if (value) {
        newSearchParams.set(param, value);
      } else {
        newSearchParams.delete(param);
      }
      history.push({
        pathname: location.pathname,
        search: newSearchParams.toString(),
      });
    },
    [location.pathname, location.search, history],
  );

  // Memoize the loadTags function to prevent recreation on every render
  const loadTagsMemoized = useCallback(async () => {
    if (isLoadingTags || tags) return;

    setIsLoadingTags(true);
    try {
      await loadTags(setTags, partyTags, tagApi);
    } catch (error) {
      console.error("Failed to load tags:", error);
    } finally {
      setIsLoadingTags(false);
    }
  }, [isLoadingTags, tags, tagApi]);

  interface ProgressState {
    [key: string]: number;
  }

  const hasProvidedInfo = useMemo(() => {
    return !!(
      user?.firstName &&
      user?.lastName &&
      user?.barNumber &&
      (!user?.role?.includes(Role.FirmAdmin) ||
        (firm?.name && firm?.address?.street && firm?.address.postalCode))
    );
  }, [
    user?.firstName,
    user?.lastName,
    user?.barNumber,
    user?.role,
    firm?.name,
    firm?.address?.street,
    firm?.address?.postalCode,
  ]);

  const missingRequiredInfo = useMemo(() => {
    return !quickActionCase?.caseType || !quickActionCase?.clientPosition;
  }, [quickActionCase]);
  const [estimatedProgress, setEstimatedProgress] = useState<ProgressState>({});

  function setProgress(id: string, progress: number) {
    setEstimatedProgress((prevProgress) => ({
      ...prevProgress,
      [id]: progress,
    }));
  }
  function noOp() {}

  const getFirmJx = useMemo(() => {
    if (!jurisdictions || !firm) {
      return undefined;
    }

    const firmJx = firm.offices?.find((x) => x.jurisdictions?.length)
      ?.jurisdictions?.[0]?.jurisdictionId;
    const jx = jurisdictions?.find((j) => j.id === firmJx);
    return jx;
  }, [jurisdictions, firm]);

  const supportsFrogs = getFirmJx?.id === CaliforniaId;
  const nonArchivedDocs = documents?.filter((x) => !x.isArchived);
  const processingDocs = useMemo(() => {
    return nonArchivedDocs
      ?.filter(
        (x) =>
          (x.status || 0) < DocumentStatus.Processed ||
          x.status === DocumentStatus.Error ||
          x.status === DocumentStatus.Processed,
      )
      .sort((a, b) => {
        if (a.uploadDate < b.uploadDate) return 1;
        if (a.uploadDate > b.uploadDate) return -1;

        return a.id.localeCompare(b.id);
      });
  }, [nonArchivedDocs]);

  const processingDocuments = useMemo(() => {
    return nonArchivedDocs?.filter(
      (d) => (d.status || 0) < DocumentStatus.Processed,
    );
  }, [nonArchivedDocs]);

  let tempConfirmedDocs = useMemo(() => {
    return nonArchivedDocs?.filter(
      (d) =>
        d.status === DocumentStatus.InfoConfirmed ||
        d.status === DocumentStatus.Complete,
    );
  }, [nonArchivedDocs]);

  const recentComms = useMemo(() => {
    return tempConfirmedDocs?.filter((d) => {
      const comm = communications?.find((x) => x.objectId === d.id);
      return (
        comm?.lastActivityDate &&
        comm.status === CommunicationStatus.Complete &&
        (!d.info?.lastViewDate || comm.lastActivityDate < d.info.lastViewDate)
      );
    });
  }, [tempConfirmedDocs, communications]);

  tempConfirmedDocs = useMemo(() => {
    return tempConfirmedDocs?.filter((d) => {
      const comm = communications?.find((x) => x.objectId === d.id);
      return (
        !comm?.lastActivityDate ||
        comm.status !== CommunicationStatus.Complete ||
        (d.info?.lastViewDate && comm.lastActivityDate > d.info.lastViewDate)
      );
    });
  }, [tempConfirmedDocs, communications]);

  async function updateDocCaseClient(
    doc: DocumentClient,
    _case?: Case,
    client?: Client,
  ) {
    await updateDocument(doc);
    if (_case?.id && !allCases?.find((x) => x.id === _case.id)) {
      setAllCases((a) => (a ? [...a, _case] : [_case]));
    }

    if (client?.id && !allClients?.find((x) => x.id === client.id)) {
      setAllClients((a) => (a ? [...a, client] : [client]));
    }
  }

  // Memoize the interval condition to prevent unnecessary re-renders
  const shouldPoll = useMemo(() => {
    return processingDocuments && processingDocuments.length > 0;
  }, [processingDocuments]);

  UseInterval(loadDocuments, shouldPoll ? 1000 : null);

  function handleChooseFile(e: React.MouseEvent) {
    e.preventDefault();
    fileInputRef?.current?.click();
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    await handleDropFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    setIsOver(true);
    e.preventDefault();
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    setIsOver(false);
    e.preventDefault();
  };

  async function handleRequiredCase(_case: Case) {
    if (!user) throw new Error("User firmId is required");
    if (!_case.id) throw new Error("_case.id is required");
    await caseApi.casePut({ firmId: user.firmId, id: _case.id, _case });
    setQuickActionCase(_case);
    await loadTagsMemoized();
  }

  const handleDismissTutorial = useCallback(() => {
    setIsIntroTutorialModalShow(false);

    if (user?.preferences) {
      user.preferences.hasSeenIntroTutorial = true;
      updatePreferences(user.preferences);
    }
  }, [user, updatePreferences]);

  const loadCommunication = useCallback(async () => {
    try {
      const coms = (await communicationApi.communicationGetFirmCommunications({
        lowerBound: CommunicationStatus.Pending,
        upperBound: CommunicationStatus.Complete,
      })) as ViewCommunicationResponse[];
      setCommunications(coms);
    } catch (error) {
      rg4js("send", {
        error: normalizeError(error),
      });
      throw new Error("There was an issue loading communications.");
    }
  }, [communicationApi]);

  // Memoize the communication loading condition
  const shouldLoadCommunications = useMemo(() => {
    return !communications;
  }, [communications]);

  useEffect(() => {
    if (shouldLoadCommunications) {
      loadCommunication();
    }
  }, [shouldLoadCommunications, loadCommunication]);

  // Memoize the tutorial modal condition
  const shouldShowIntroTutorial = useMemo(() => {
    if (!user) return false;
    if (isSignUpModalQuery) return false;
    return !!user?.preferences && !user.preferences.hasSeenIntroTutorial;
  }, [user, isSignUpModalQuery]);

  useEffect(() => {
    if (user) {
      if (!isSignUpModalQuery) {
        setIsIntroTutorialModalShow(shouldShowIntroTutorial);
      } else {
        handleDismissTutorial();
      }
    }
  }, [
    user,
    isSignUpModalQuery,
    shouldShowIntroTutorial,
    handleDismissTutorial,
  ]);

  // Memoize the signup window condition
  const shouldShowSignUpWindow = useMemo(() => {
    if (!user || !firm) return false;
    const query = sessionStorage.getItem("showSignUp");
    return !!(
      query &&
      !localStorage.getItem(hasSeenSignUpModalStorageKey) &&
      firm.billingInfo?.subscriptionStatus !== SubscriptionStatus.Active
    );
  }, [user, firm]);

  useEffect(() => {
    if (shouldShowSignUpWindow) {
      setIsSignUpModalShow(true);
      setIsSignUpModalQuery(true);
    }
  }, [shouldShowSignUpWindow]);

  // Memoize the data loading condition
  const shouldLoadData = useMemo(() => {
    return !infoLoading.current && user && !allCases && !allClients;
  }, [user, allCases, allClients]);

  // Memoize the API calls to prevent recreation on every render
  const loadCasesAndClients = useCallback(async () => {
    if (!shouldLoadData || !user) return;

    infoLoading.current = true;
    try {
      const [cases, clients] = await Promise.all([
        (await firmApi.firmGetFirmCases({
          firmId: user.firmId,
        })) as CaseResponse[],
        (await clientApi.clientGetAll({
          firmId: user.firmId,
        })) as ClientResponse[],
      ]);
      setAllCases(cases);
      setAllClients(clients);
    } catch (error) {
      console.error("Failed to load cases and clients:", error);
    } finally {
      infoLoading.current = false;
    }
  }, [shouldLoadData, user, firmApi, clientApi]);

  useEffect(() => {
    loadCasesAndClients();
  }, [loadCasesAndClients]);

  async function handleUpload(fileList: FileList) {
    if (!firm) throw new Error("Firm is required");
    if (user?.isInactive || hasCanceledSubscription(firm)) {
      showSignupPage();
      return;
    }
    if (showPropoundIntroModal) setShowPropoundIntroModal(false);
    setError("");
    const files = Array.from(fileList);
    if (files.length) {
      if (isUploadingDiscovery) {
        updateSearchParam(SEARCH_PARAMS.PROPOUND_DISCOVERY);
      }
      const sanitizedFiles = sanitizeFiles(files);
      if (sanitizedFiles.length) {
        const newUploadingDocs = sanitizedFiles.map((file) =>
          getUploadingDocument(file.name),
        );
        setUploadingDocuments([...uploadingDocuments, ...newUploadingDocs]);
        await uploadFiles(sanitizedFiles, undefined, isPleadingUpload);
        const remainingUploadingDocs = uploadingDocuments.filter(
          (doc) => !newUploadingDocs.includes(doc),
        );
        setUploadingDocuments(remainingUploadingDocs);
      } else {
        setError("Only .pdf files are currently supported.");
      }
    }
    if (isPleadingUpload) setIsPleadingUpload(false);
    if (isUploadingResponse) {
      updateSearchParam(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY);
    }
  }

  async function handleDropFiles(files: FileList) {
    handleUpload(files);
    setIsOver(false);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (files) {
      handleUpload(files);
    }
    e.target.value = "";
    if (isUploadingDiscovery) {
      updateSearchParam(SEARCH_PARAMS.PROPOUND_DISCOVERY);
    }
    if (isUploadingResponse) {
      updateSearchParam(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY);
    }
  }

  const externalUploadRefresh = useCallback(() => {
    setShowUploadDocs(false);
  }, []);

  function showSignupPage() {
    rg4js("trackEvent", {
      type: "pageView",
      path: "/signup-modal",
    });
    setIsSignUpModalShow(true);
  }

  async function handleCaseClientCreation(newCaseInfo?: Case) {
    if (!user) throw new Error("User is required");
    // Case creation
    let newCase = newCaseInfo || quickActionCase;
    if (!newCase) throw new Error("Case is required");

    if (isExternalEdit) {
      try {
        if (!newCase.id) throw new Error("Case ID is required");
        await caseApi.casePut({
          firmId: user.firmId,
          id: newCase.id,
          _case: newCase,
        });
      } catch (error) {
        rg4js("send", {
          error: normalizeError(error),
        });
        throw new Error("There was an issue updating the external case.");
      }

      await loadTagsMemoized();
      setQuickActionCase(newCase);
      setTypeToGenerate(quickActionType);
      setQuickActionIsSaving(false);
      setShowAddNewCase(false);
    } else {
      if (newCaseInfo) {
        try {
          newCase = (await caseApi.casePost({
            firmId: user.firmId,
            _case: newCase,
          })) as CaseResponse;

          if (newCaseInfo?.integration?.identifier) {
            if (!newCase.id) throw new Error("Case ID is required");
            await caseApi.caseExternalIntegration({
              firmId: user.firmId,
              id: newCase.id,
              externalIntegration: newCase.integration,
            });
          }

          setQuickActionCase(newCase);
        } catch (error) {
          rg4js("send", {
            error: normalizeError(error),
          });
          throw new Error("There was an issue creating a new case.");
        }
      }

      try {
        await loadTagsMemoized();
        setTypeToGenerate(quickActionType);
        setQuickActionIsSaving(false);
        setShowAddNewCase(false);
        updateSearchParam(SEARCH_PARAMS.UPLOAD_PRODUCTION);
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set(SEARCH_PARAMS.LABEL.TAB, SEARCH_PARAMS.PRODUCTION);
        history.push({
          pathname: `/cases/${newCase?.id}`,
          search: `?${newSearchParams.toString()}`,
        });
      } catch (error) {
        rg4js("send", {
          error: normalizeError(error),
        });
        throw new Error("There was an issue adding client to the case.");
      }
    }
  }

  async function handleSelectGenerate(): Promise<void> {
    if (!firm) throw new Error("Firm is required");
    pendingDraftType.current = undefined;
    setQuickActionIsSaving(true);
    // show info page if user has not provided info yet, else generate word document
    if (hasProvidedInfo) {
      if (!user?.isInactive && isInTrialOrHasActiveSubscription(firm)) {
        await loadTagsMemoized();
        if (
          quickActionCase?.integration?.partner &&
          (quickActionCase.jurisdiction === blankId ||
            quickActionCase.venue === blankId)
        ) {
          setIsExternalEdit(true);
          setShowAddNewCase(true);
          updateSearchParam(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY);
          return;
        }
        // Client exists, proceeds the flow
        if (quickActionCase?.id) {
          handleCaseClientCreation();
          // Creates a new case
        } else {
          setShowAddNewCase(true);
        }
      } else {
        showSignupPage();
      }
      setQuickActionIsSaving(false);
      updateSearchParam(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY);
    }
  }

  function handlePropoundModalInit() {
    updateSearchParam(SEARCH_PARAMS.PROPOUND_DISCOVERY, "true");
  }

  function handlePropoundModalDismiss() {
    setIsPleadingUpload(false);
    updateSearchParam(SEARCH_PARAMS.PROPOUND_DISCOVERY);
  }

  function handleQuickActionModal(isProductionUploadModal?: boolean) {
    if (!firm) throw new Error("Firm is required");

    if (!hasProvidedInfo) {
      setShowInfoModal(true);
      return;
    }

    if (
      hasProvidedInfo &&
      !user?.isInactive &&
      isInTrialOrHasActiveSubscription(firm)
    ) {
      if (isProductionUploadModal) {
        updateSearchParam(SEARCH_PARAMS.UPLOAD_PRODUCTION, "true");
      } else {
        updateSearchParam(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY, "true");
      }
    } else {
      showSignupPage();
    }
  }

  function handleQuickActionExternalUpload() {
    handleCaseCreateDismiss();
    setShowUploadDocs(true);
  }

  function handleUploadProductionDismiss() {
    updateSearchParam(SEARCH_PARAMS.UPLOAD_PRODUCTION);
    setQuickActionCase(undefined);
    setShowAddNewCase(false);
  }

  function handleCaseCreateDismiss() {
    setIsExternalEdit(false);
    setQuickActionCase(undefined);
    setQuickActionType(undefined);
    setQuickActionIsSaving(false);
    setTags(undefined);
    setTypeToGenerate(undefined);
    setShowAddNewCase(false);
    setIsPleadingUpload(false);
    if (isUploadingProduction) {
      updateSearchParam(SEARCH_PARAMS.UPLOAD_PRODUCTION);
    }
    if (isUploadingResponse) {
      updateSearchParam(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY);
    }
  }

  function handleDismissSignUp() {
    localStorage.setItem(hasSeenSignUpModalStorageKey, "true");
    setIsSignUpModalShow(false);
  }

  async function handleRemoveClientAccess(
    communication: ViewCommunicationResponse,
  ) {
    try {
      await communicationApi.communicationUpdateCommunication({
        id: communication.id,
        putClientCommunication: {
          ...communication,
          sharedWithObjectType: undefined,
          sharedWithObjectId: undefined,
          status: CommunicationStatus.Closed,
        },
      });
      setCommunications(
        communications?.filter((c) => c.id !== communication.id),
      );
    } catch (error) {
      rg4js("send", {
        error: normalizeError(error),
      });
    }
  }

  async function handleUpdateCommunication(
    communication: ViewCommunicationResponse,
  ) {
    const resp = (await communicationApi.communicationUpdateCommunication({
      id: communication.id,
      putClientCommunication: { ...communication },
    })) as ViewCommunicationResponse;

    setCommunications(
      communications?.map((comm) =>
        comm.id === communication.id
          ? {
              ...resp,
              externalCommunicationItems: comm.externalCommunicationItems,
            }
          : comm,
      ),
    );
  }

  async function handleArchiveDocument(doc: DocumentClient) {
    const commsToArchive =
      communications?.filter(
        (c) => c.objectId === doc.id && c.status !== CommunicationStatus.Closed,
      ) ?? [];
    for (const com of commsToArchive) {
      await communicationApi.communicationUpdateCommunication({
        id: com.id,
        putClientCommunication: {
          ...com,
          status: CommunicationStatus.Closed,
        },
      });
      setCommunications(communications?.filter((c) => c.id !== com.id));
    }

    await archiveFilesMutation.mutateAsync(doc);
  }

  const trialEndsMessage = GetTrialBannerMessage(user, firm);
  const showCountdownStyle = !!firm?.billingInfo?.creditsExpireDate;
  const trialEnds = (
    <TrialEndsBanner
      onClickSignUp={() => showSignupPage()}
      message={trialEndsMessage}
      showCountdownStyle={showCountdownStyle}
    />
  );

  return (
    <>
      <LeavingGuard
        when={uploadingDocuments.length > 0}
        message="Your upload will stop if you leave this page."
        title="Upload in progress!"
      />
      <Container className="page-container">
        {/* <Alert variant="danger">Briefpoint is currently experiencing a service outage preventing newly uploaded documents from processing, we apologize for the inconvenience and will remove this banner when the issue is resolved.</Alert> */}
        {trialEnds}
        {error && <Alert variant="danger">{error}</Alert>}
        {didSignUp && (
          <Alert variant="success">Success! Signup complete.</Alert>
        )}

        <TopRibbon
          handleQuickActionUpload={() => handleQuickActionModal()}
          handlePropoundIntroModal={handlePropoundModalInit}
          handleUploadProduction={() => handleQuickActionModal(true)}
        />

        {uploadingDocuments &&
          uploadingDocuments.length > 0 &&
          uploadingDocuments.map((doc) => (
            <ProcessingBox
              key={doc.id}
              document={doc}
              processingType="uploading"
              estimatedProgress={0}
              setEstimatedProgress={noOp}
              onDelete={loadDocuments}
            />
          ))}
        {processingDocs &&
          processingDocs.length > 0 &&
          processingDocs.map((doc) => (
            <ProcessingBox
              key={doc.id}
              document={doc}
              estimatedProgress={
                //estimatedProgress == parsingProgress * 100
                (estimatedProgress[doc.id] ?? 0) >
                (doc.info?.parsingProgress ??
                  (doc.status === DocumentStatus.Processed ? 1 : 0)) *
                  100
                  ? estimatedProgress[doc.id]
                  : (doc.info?.parsingProgress ??
                      (doc.status === DocumentStatus.Processed ? 1 : 0)) * 100
              }
              setEstimatedProgress={setProgress}
              onDelete={loadDocuments}
            />
          ))}
        {!recentComms?.length && (
          <header className="d-flex justify-content-end align-items-center">
            <div
              className={`${tempConfirmedDocs?.length ? "document-actions" : "document-actions mb-3"}`}
            >
              <ExternalPartnerActions
                toggleOnly={false}
                handleQuickActionExternalUpload={
                  handleQuickActionExternalUpload
                }
                setShowUploadDocs={setShowUploadDocs}
                handleChooseFile={handleChooseFile}
              />
              <Help
                id="document-help"
                text={`Briefpoint currently accepts the following state court documents: <ul><li class="tooltip-row">Requests for Admission</li><li class="tooltip-row">Requests for Production</li><li class="tooltip-row">${supportsFrogs ? "Special " : ""}Interrogatories</li>${
                  supportsFrogs
                    ? '<li class="tooltip-row">Form Interrogatories</li>'
                    : ""
                }</ul>`}
                direction={"bottom"}
              />
            </div>
            <input
              type="file"
              id="file-input"
              name="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="d-none"
              multiple
            />
          </header>
        )}
        {/* <h2 className="mt-5">Recent Documents</h2> */}
        {/* <hr /> */}
        <Loading
          className="parentDocList"
          isLoading={documents === null || communications === null}
        >
          {recentComms?.length ? (
            <>
              <header className="d-flex justify-content-between mt-3 mb-3 align-items-center">
                <h4 className="table-headers">Recent Client Activity</h4>
                <div
                  className={`${tempConfirmedDocs?.length ? "document-actions" : "document-actions mb-3"}`}
                >
                  <ExternalPartnerActions
                    toggleOnly={false}
                    handleQuickActionExternalUpload={
                      handleQuickActionExternalUpload
                    }
                    setShowUploadDocs={setShowUploadDocs}
                    handleChooseFile={handleChooseFile}
                  />
                  <Help
                    id="document-help"
                    text={`Briefpoint currently accepts the following state court documents: <ul><li class="tooltip-row">Requests for Admission</li><li class="tooltip-row">Requests for Production</li><li class="tooltip-row">${supportsFrogs ? "Special " : ""}Interrogatories</li>${
                      supportsFrogs
                        ? '<li class="tooltip-row">Form Interrogatories</li>'
                        : ""
                    }</ul>`}
                    direction={"bottom"}
                  />
                </div>
                <input
                  type="file"
                  id="file-input"
                  name="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="d-none"
                  multiple
                />
              </header>
              <DocumentList
                documents={recentComms}
                archiveDocument={handleArchiveDocument}
                updateDocument={updateDocCaseClient}
                allCases={allCases}
                allClients={allClients}
                downloadForm={downloadForm}
                downloadRequest={downloadRequest}
                externalCommunications={communications}
                removeClientAccess={handleRemoveClientAccess}
                updateCommunication={handleUpdateCommunication}
              />
            </>
          ) : (
            ""
          )}
          <div
            className={clsx("documents-wrapper", { dropping: isOver })}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
          >
            {tempConfirmedDocs?.length ? (
              <>
                <h4
                  className="table-headers"
                  data-testid="document-list-header"
                >
                  Document List
                </h4>
                <DocumentList
                  documents={tempConfirmedDocs ?? []}
                  archiveDocument={handleArchiveDocument}
                  updateDocument={updateDocCaseClient}
                  allCases={allCases}
                  allClients={allClients}
                  downloadForm={downloadForm}
                  downloadRequest={downloadRequest}
                  externalCommunications={communications}
                  removeClientAccess={handleRemoveClientAccess}
                  updateCommunication={handleUpdateCommunication}
                />
              </>
            ) : (
              <EmptyList
                chooseFile={handleChooseFile}
                supportsFrogs={getFirmJx?.id === CaliforniaId}
              />
            )}
            <p className="archive">
              <Link to="/documents/archive">View Archived Documents</Link>
            </p>
          </div>
        </Loading>
        {isUploadingResponse && (
          <RespondToDiscoveryModal
            isShow={isUploadingResponse}
            quickActionIsSaving={quickActionIsSaving}
            getFirmJx={getFirmJx}
            handleQuickActionExternalUpload={handleQuickActionExternalUpload}
            onClose={handleCaseCreateDismiss}
            onConfirm={handleSelectGenerate}
            setShowUploadDocs={setShowUploadDocs}
            handleChooseFile={handleChooseFile}
          />
        )}
        {quickActionCase && jurisdictions && missingRequiredInfo && (
          <RequiredCaseInfo
            show={!!typeToGenerate && missingRequiredInfo}
            onClose={handleCaseCreateDismiss}
            onConfirm={handleRequiredCase}
            _case={quickActionCase}
            jurisdictions={jurisdictions}
          />
        )}
        {trialEndsMessage && (
          <SignUpModal
            isShow={isSignUpModalShow}
            onClose={handleDismissSignUp}
          />
        )}
        {showAddNewCase && firm && (
          <AddEditCaseModal
            title="Add Case"
            confirm="Add"
            availableTags={tags}
            setAvailableTags={setTags}
            isShow={showAddNewCase}
            users={users || []}
            jurisdictions={jurisdictions || []}
            _case={quickActionCase}
            newCaseTitle={quickActionCase?.shortTitle ?? ""}
            onClose={handleCaseCreateDismiss}
            onConfirm={handleCaseClientCreation}
          />
        )}
        <IntroTutorialModal
          isShow={isIntroTutorialModalShow}
          supportsFrogs={supportsFrogs}
          onClose={handleDismissTutorial}
          fileUpload={handleChooseFile}
        />
        {isUploadingDiscovery && (
          <PropoundSelectionModal
            isShow={isUploadingDiscovery}
            onClose={handlePropoundModalDismiss}
            handleQuickActionModal={handleQuickActionModal}
            handleQuickActionUpload={handleChooseFile}
            setShowPropoundIntroModal={setShowPropoundIntroModal}
            setIsPleadingUpload={setIsPleadingUpload}
          />
        )}
        {isUploadingProduction && (
          <UploadProductionModal
            isShow={isUploadingProduction}
            setShowUploadDocs={setShowUploadDocs}
            handleChooseFile={handleChooseFile}
            onClose={handleUploadProductionDismiss}
            quickActionCase={quickActionCase}
            setQuickActionCase={setQuickActionCase}
            quickActionIsSaving={quickActionIsSaving}
            isExternalConnectionActive={user?.externalConnection?.isActive}
            cases={allCases}
            formError={quickActionError}
            setFormError={setQuickActionError}
            handleQuickActionExternalUpload={handleQuickActionExternalUpload}
            onConfirm={handleSelectGenerate}
          />
        )}
        {!hasProvidedInfo && (
          <Modal
            show={showInfoModal}
            onClose={() => setShowInfoModal(false)}
            size="xl"
            title={"Information for output document"}
            showCancelButton={false}
            showConfirmButton={false}
            preventBackgroundClick
          >
            <GeneralInformationScreen
              documentId={"anything"}
              onSave={async () => {
                setShowInfoModal(false);
                updateSearchParam(SEARCH_PARAMS.RESPONSE_TO_DISCOVERY, "true");
              }}
            />
          </Modal>
        )}
        {user?.externalConnection && (
          <Modal
            onClose={() => setShowUploadDocs(false)}
            className="externalModal"
            show={showUploadDocs}
            title="Upload Files"
            confirmText="Upload Files"
          >
            <ExternalFileBrowserSelect
              cases={allCases}
              setIsExternalUploading={setIsExternalUploading}
              isExternalUploading={isExternalUploading}
              loadDocuments={loadDocuments}
              onFinish={externalUploadRefresh}
            />
          </Modal>
        )}
      </Container>
    </>
  );
}
